/**
 * SuperAdmin Centre Insights - request detail panel and Payouts section. READ-ONLY.
 * GET /api/superadmin/centre-insights/requests/:id  and  /payouts
 * Both components load only while they are mounted: the parent mounts the panel when a request is chosen and the section
 * when a Payouts line is clicked, and unmounts them on close or when another centre is chosen. Stale responses are
 * ignored. The shared styles and helpers (S, money, day, errText, Line) come from CentreInsights.js through the ui prop.
 */

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import api from '../../utils/api';

const BASE = '/superadmin/centre-insights';
const PAY_CHIPS = [['paid', 'Paid'], ['ready', 'Ready to pay'], ['on_hold', 'On hold']];
const words = v => String(v || '-').replace(/_/g, ' ');
const holdText = r => [r.patientHold && 'Patient concern', r.adminHold && 'Admin'].filter(Boolean).join(', ');

export function RequestPanel({ ui, tenantId, requestId, onClose }) {
  const { S, money, day, errText, Line } = ui;
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true; // false after cleanup, so a stale or unmounted response is ignored
    setState({ loading: true, error: '', data: null });
    (async () => {
      try {
        const res = await api.get(`${BASE}/requests/${requestId}`, { params: { tenantId } });
        if (current) setState({ loading: false, error: '', data: res.data.data });
      } catch (e) { if (current) setState({ loading: false, error: errText(e), data: null }); }
    })();
    return () => { current = false; };
  }, [tenantId, requestId, attempt, errText]);

  const d = state.data;
  return (
    <div style={S.panel} role="dialog" aria-label="Request details">
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '16px' }}>{d ? d.patientName || 'Unknown patient' : 'Request details'}</strong>
        <button type="button" style={S.btn} onClick={onClose} aria-label="Close"><FiX /></button>
      </div>
      <div style={S.note}>Opening a request is recorded in the audit log.</div>
      {state.loading && <p>Loading...</p>}
      {state.error && <div style={S.error}>{state.error} <button type="button" style={S.btn} onClick={() => setAttempt(a => a + 1)}>Retry</button></div>}
      {d && <div style={{ marginTop: '12px' }}>
        <Line label="Type" value={d.patientType === 'walk_in' ? 'Walk-in' : 'Registered'} />
        <Line label="Phone" value={d.phone || '-'} />
        <Line label="Status" value={words(d.status)} />
        <Line label="Healer" value={d.healerName || 'Not assigned'} />
        <Line label="Source" value={words(d.source)} />
        <Line label="Date" value={day(d.createdAt)} />
        <div style={{ ...S.cardTitle, marginTop: '12px' }}>Condition</div>
        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{d.condition || '-'}</div>
        <div style={{ ...S.cardTitle, marginTop: '12px' }}>Payment</div>
        <Line label="Status" value={words(d.payment.status)} />
        <Line label="Amount" value={d.payment.amountRupees === null ? '-' : money(d.payment.amountRupees)} />
        <Line label="Method" value={words(d.payment.method)} />
      </div>}
    </div>
  );
}

export function PayoutsSection({ ui, tenantId, period, initialStatus, onClose }) {
  const { S, money, day, errText } = ui;
  const [status, setStatus] = useState(initialStatus);
  const [paging, setPaging] = useState({ scope: '', n: 1 });
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, error: '', rows: [], meta: null });

  // The period only applies to Paid, so only Paid reloads when it changes. The page goes back to 1 whenever the scope changes.
  const periodParam = status === 'paid' ? period : 'all';
  const scope = `${tenantId}|${status}|${periodParam}`;
  const page = paging.scope === scope ? paging.n : 1;

  useEffect(() => {
    let current = true; // false after cleanup, so a stale or unmounted response is ignored
    setState({ loading: true, error: '', rows: [], meta: null });
    (async () => {
      try {
        const res = await api.get(`${BASE}/payouts`, { params: { tenantId, status, period: periodParam, page } });
        if (current) setState({ loading: false, error: '', rows: res.data.data, meta: res.data.meta });
      } catch (e) { if (current) setState({ loading: false, error: errText(e), rows: [], meta: null }); }
    })();
    return () => { current = false; };
  }, [tenantId, status, periodParam, page, attempt, errText]);

  const { loading, error, rows, meta } = state;
  const goTo = n => setPaging({ scope, n });
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '16px', margin: 0 }}>Payouts</h3>
        <button type="button" style={S.btn} onClick={onClose}>Close</button>
      </div>
      <div style={S.row}>
        {PAY_CHIPS.map(([v, l]) => <button key={v} type="button" style={S.chip(status === v)} onClick={() => setStatus(v)}>{l}</button>)}
      </div>
      {meta && <div style={{ ...S.row, fontSize: '14px', fontWeight: 600 }}>
        Total: {money(meta.totalAmountRupees)} ({meta.total} {meta.total === 1 ? 'line' : 'lines'})</div>}
      {error && <div style={S.error}>{error} <button type="button" style={S.btn} onClick={() => setAttempt(a => a + 1)}>Retry</button></div>}
      {loading && <p>Loading...</p>}
      {!loading && !error && rows.length === 0 && <p style={S.note}>No payouts for this filter</p>}
      {rows.length > 0 && <div style={S.scroll}><table style={{ ...S.table, minWidth: '1000px' }}>
        <thead><tr>{['Date', 'Session', 'Healer', 'Patient', 'Session rate', 'Share %', 'Healer amount', 'Status', 'Hold', 'Payment mode']
          .map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={S.td}>{day(status === 'paid' ? r.paidAt : r.createdAt)}</td><td style={S.td}>{r.sessionNumber}</td>
            <td style={S.td}>{r.healerName || '-'}</td><td style={S.td}>{r.patientName || 'Unknown'}</td>
            <td style={S.td}>{money(r.baseAmountRupees)}</td><td style={S.td}>{r.sharePercent}%</td>
            <td style={S.td}>{money(r.amountRupees)}</td><td style={S.td}>{words(r.status)}</td>
            <td style={S.td}>{holdText(r)}</td><td style={S.td}>{words(r.paymentMode)}</td>
          </tr>))}</tbody>
      </table></div>}
      <div style={S.note}>Payouts list is recorded in the audit log.</div>
      <div style={S.note}>Period applies to Paid only. Ready to pay and On hold are current balances.</div>
      <div style={S.note}>Session rate is the configured rate, not the amount the patient paid.</div>
      {meta && meta.totalPages > 1 && <div style={{ ...S.row, marginTop: '10px' }}>
        <button type="button" style={S.btn} disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
        <span style={{ fontSize: '14px' }}>Page {meta.page} of {meta.totalPages}</span>
        <button type="button" style={S.btn} disabled={page >= meta.totalPages} onClick={() => goTo(page + 1)}>Next</button>
      </div>}
    </div>
  );
}
